<template>
  <v-app-bar
    :elevation="scrolled ? 2 : 0"
    :class="['main-navbar', { 'app-bar-scrolled': scrolled }]"
    app
    :height="$vuetify.display.mobile ? 60 : 80"
  >
    <div class="navbar-container">
      <div class="left-section d-flex align-center">
        <!-- Navigation Menu (Desktop) - Dropdown on Left -->
        <div class="nav-menu d-none d-lg-flex">
          <v-menu>
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                variant="outlined"
                color="primary"
                size="large"
                class="nav-dropdown-btn"
              >
                <v-icon class="me-2">mdi-menu</v-icon>
                {{ $t('navbar.menu') }}
                <v-icon end class="chevron-icon">mdi-chevron-down</v-icon>
              </v-btn>
            </template>

            <v-list class="nav-dropdown">
              <v-list-item
                v-for="item in navigationItems"
                :key="item.key"
                :to="item.to"
                @click="item.action && item.action()"
                :class="{ 'v-list-item--active': item.to && isActiveRoute(item.to) }"
              >
                <template v-slot:prepend>
                  <v-icon :icon="item.icon" class="me-3" />
                </template>
                <v-list-item-title>{{ $t('navbar.' + item.key) }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>

        <!-- Logo Section -->
        <div class="logo-section">
        <router-link to="/" class="logo-link">
          <!-- Desktop: Show logo and text -->
          <div class="d-none d-md-flex align-center">
            <v-img
              :src="logoSrc"
              alt="Көркем Авто"
              width="50"
              height="50"
              class="logo"
            />
            <div class="logo-text">
              <h3 class="logo-title">Көркем Авто</h3>
              <p class="logo-subtitle">{{ $t('navbar.subtitle') }}</p>
            </div>
          </div>
          
          <!-- Mobile: Logo + version connected pill -->
          <div class="d-md-none mobile-home-icon mobile-logo-pill">
            <v-tooltip location="bottom">
              <template v-slot:activator="{ props }">
                <v-img
                  v-bind="props"
                  :src="logoSrc"
                  alt="Көркем Авто"
                  width="32"
                  height="32"
                  class="mobile-logo"
                />
              </template>
              <span>{{ $t('navbar.home') }}</span>
            </v-tooltip>
            <span class="mobile-version-badge" aria-label="App version">
              v{{ appVersion }}
            </span>
          </div>
        </router-link>
      </div>
    </div>

    <!-- User Actions -->
    <div class="user-actions">
        <!-- Theme Switcher -->
        <ThemeSwitcher />

        <!-- Language Switcher -->
        <LanguageSwitcher />

        <!-- User Menu -->
        <div v-if="user" class="user-menu">
          <v-menu>
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                variant="outlined"
                color="primary"
                size="small"
                :icon="!$vuetify.display.mdAndUp"
                class="user-btn user-btn-pill"
              >
                <!-- Mobile: icon only (matches theme/lang shape) -->
                <template v-if="!$vuetify.display.mdAndUp">
                  <v-img
                    v-if="user.avatar"
                    :src="user.avatar"
                    :alt="user.firstName"
                    class="user-btn-avatar-img"
                  />
                  <v-icon v-else>mdi-account</v-icon>
                </template>

                <!-- Desktop: avatar + name + chevron -->
                <template v-else>
                  <v-avatar size="28" class="me-2">
                    <v-img
                      v-if="user.avatar"
                      :src="user.avatar"
                      :alt="user.firstName"
                    />
                    <v-icon v-else color="primary">mdi-account</v-icon>
                  </v-avatar>
                  <div class="user-info">
                    <p class="user-name">{{ getShortName(user.fio || `${user.firstName} ${user.lastName}`) }}</p>
                    <p class="user-role">{{ $t('roles.' + user.role) }}</p>
                  </div>
                  <v-icon end size="18">mdi-chevron-down</v-icon>
                </template>
              </v-btn>
            </template>

            <v-list class="user-dropdown">
              <v-list-item
                v-for="item in userMenuItems"
                :key="item.key"
                :to="item.to"
                @click="item.action && item.action()"
              >
                <template v-slot:prepend>
                  <v-icon>{{ item.icon }}</v-icon>
                </template>
                <v-list-item-title>{{ $t('navbar.' + item.key) }}</v-list-item-title>
              </v-list-item>
              
              <v-divider />
              
              <v-list-item @click="logout" class="logout-item">
                <template v-slot:prepend>
                  <v-icon color="error">mdi-logout</v-icon>
                </template>
                <v-list-item-title class="text-error">
                  {{ $t('navbar.logout') }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>

        <!-- Auth Buttons -->
        <div v-else class="auth-buttons d-none d-md-flex">
          <v-btn
            to="/login"
            variant="outlined"
            color="primary"
            class="me-2"
          >
            {{ $t('navbar.login') }}
          </v-btn>
          <v-btn
            to="/register"
            variant="flat"
            color="primary"
          >
            {{ $t('navbar.register') }}
          </v-btn>
        </div>

        <!-- Mobile Menu Button -->
        <v-btn
          variant="text"
          icon
          class="d-lg-none mobile-menu-btn"
          @click="toggleMobileMenu"
        >
          <v-icon>mdi-menu</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Mobile Navigation Dialog (Alternative approach) -->
  <v-dialog
    v-model="mobileMenuOpen"
    fullscreen
    hide-overlay
    transition="slide-x-reverse-transition"
    class="mobile-menu-dialog"
  >
    <v-card class="mobile-menu-card">
      <v-toolbar color="primary" dark>
        <v-toolbar-title>Меню</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          icon
          @click="mobileMenuOpen = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <div class="mobile-header pa-4">
        <div v-if="user" class="mobile-user-info">
          <v-avatar size="60" class="mb-3">
            <v-img
              v-if="user.avatar"
              :src="user.avatar"
              :alt="user.firstName"
            />
            <v-icon v-else color="primary" size="30">mdi-account</v-icon>
          </v-avatar>
          <h4>{{ user.firstName }} {{ user.lastName }}</h4>
          <p class="text-grey">{{ $t('roles.' + user.role) }}</p>
        </div>
        
        <div v-else class="mobile-auth-buttons">
          <v-btn
            to="/login"
            variant="outlined"
            color="primary"
            block
            class="mb-2"
            @click="mobileMenuOpen = false"
          >
            {{ $t('navbar.login') }}
          </v-btn>
          <v-btn
            to="/register"
            variant="flat"
            color="primary"
            block
            @click="mobileMenuOpen = false"
          >
            {{ $t('navbar.register') }}
          </v-btn>
        </div>
      </div>

      <v-divider />

      <v-list>
        <v-list-item
          v-for="item in navigationItems"
          :key="item.key"
          :to="item.to"
          @click="mobileMenuOpen = false"
        >
          <template v-slot:prepend>
            <v-icon>{{ item.icon }}</v-icon>
          </template>
          <v-list-item-title>{{ $t('navbar.' + item.key) }}</v-list-item-title>
        </v-list-item>

        <div v-if="user">
          <v-divider class="my-2" />
          
          <v-list-item
            v-for="item in userMenuItems"
            :key="item.key"
            :to="item.to"
            @click="handleMobileMenuClick(item)"
          >
            <template v-slot:prepend>
              <v-icon>{{ item.icon }}</v-icon>
            </template>
            <v-list-item-title>{{ $t('navbar.' + item.key) }}</v-list-item-title>
          </v-list-item>
          
          <v-list-item @click="logout" class="logout-item">
            <template v-slot:prepend>
              <v-icon color="error">mdi-logout</v-icon>
            </template>
            <v-list-item-title class="text-error">
              {{ $t('navbar.logout') }}
            </v-list-item-title>
          </v-list-item>
        </div>
      </v-list>
    </v-card>
  </v-dialog>
  </v-app-bar>

  <!-- Mobile Navigation Dialog -->
  <v-dialog
    v-model="mobileMenuOpen"
    fullscreen
    hide-overlay
    transition="slide-x-reverse-transition"
    class="mobile-menu-dialog"
  >
    <v-card class="mobile-menu-card">
      <v-toolbar color="primary" dark>
        <v-toolbar-title>Меню</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          icon
          @click="mobileMenuOpen = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <div class="mobile-header pa-4">
        <div v-if="user" class="mobile-user-info text-center">
          <v-avatar size="60" class="mb-3">
            <v-img
              v-if="user.avatar"
              :src="user.avatar"
              :alt="user.firstName"
            />
            <v-icon v-else color="primary" size="30">mdi-account</v-icon>
          </v-avatar>
          <h4>{{ user.firstName }} {{ user.lastName }}</h4>
          <p class="text-grey">{{ $t('roles.' + user.role) }}</p>
        </div>
        
        <div v-else class="mobile-auth-buttons">
          <v-btn
            to="/login"
            variant="outlined"
            color="primary"
            block
            class="mb-2"
            @click="mobileMenuOpen = false"
          >
            {{ $t('navbar.login') }}
          </v-btn>
          <v-btn
            to="/register"
            variant="flat"
            color="primary"
            block
            @click="mobileMenuOpen = false"
          >
            {{ $t('navbar.register') }}
          </v-btn>
        </div>
      </div>

      <v-divider />

      <v-list>
        <v-list-item
          v-for="item in navigationItems"
          :key="item.key"
          :to="item.to"
          @click="mobileMenuOpen = false"
        >
          <template v-slot:prepend>
            <v-icon>{{ item.icon }}</v-icon>
          </template>
          <v-list-item-title>{{ $t('navbar.' + item.key) }}</v-list-item-title>
        </v-list-item>

        <div v-if="user">
          <v-divider class="my-2" />
          
          <v-list-item
            v-for="item in userMenuItems"
            :key="item.key"
            :to="item.to"
            @click="handleMobileMenuClick(item)"
          >
            <template v-slot:prepend>
              <v-icon>{{ item.icon }}</v-icon>
            </template>
            <v-list-item-title>{{ $t('navbar.' + item.key) }}</v-list-item-title>
          </v-list-item>
          
          <v-list-item @click="logout" class="logout-item">
            <template v-slot:prepend>
              <v-icon color="error">mdi-logout</v-icon>
            </template>
            <v-list-item-title class="text-error">
              {{ $t('navbar.logout') }}
            </v-list-item-title>
          </v-list-item>
        </div>
      </v-list>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useSnackbarStore } from '@/stores/snackbar'
import LanguageSwitcher from '@/components/common/LanguageSwitcher.vue'
import ThemeSwitcher from '@/components/common/ThemeSwitcher.vue'
import { withBasePath } from '@/utils/path'

// Composables
const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const { t } = useI18n()
const snackbar = useSnackbarStore()

// Reactive data
const scrolled = ref(false)
const mobileMenuOpen = ref(false)
const logoSrc = withBasePath('/logo.svg')
const appVersion = __APP_VERSION__

// Computed
const user = computed(() => authStore.user)

const navigationItems = computed(() => [
  {
    key: 'home',
    to: '/',
    icon: 'mdi-home'
  },
  {
    key: 'tests',
    to: (() => {
      if (!authStore.isAuthenticated) return '/tests'
      
      const role = authStore.user?.role
      switch (role) {
        case 'STUDENT':
          return '/student/tests'
        case 'ADMIN':
        case 'ADMIN_LIMITED':
          return '/admin/test-system'
        case 'THEORY_TEACHER':
          return '/teacher/tests'
        case 'LIMITED_STUDENT':
          return null
        case 'INSTRUCTOR':
          return '/tests' // Инструктор үшін публикалық тест беті
        default:
          return '/tests'
      }
    })(),
    icon: 'mdi-clipboard-text',
    disabled: authStore.user?.role === 'LIMITED_STUDENT'
  },
  {
    key: 'avtomektep',
    action: () => window.open('https://avtomektep.kz/integration/find', '_blank'),
    icon: 'mdi-car-multiple'
  },
  {
    key: 'instructors',
    to: '/instructors',
    icon: 'mdi-account-group'
  },
  {
    key: 'about',
    action: () => navigateToSection('about'),
    icon: 'mdi-information'
  },
  {
    key: 'contact',
    to: '/contact',
    icon: 'mdi-phone'
  }
])

const userMenuItems = computed(() => {
  const getDashboardRoute = () => {
    const role = user.value?.role
    switch (role) {
      case 'ADMIN':
        return '/admin/dashboard'
      case 'INSTRUCTOR':
        return '/instructor/dashboard'
      case 'THEORY_TEACHER':
        return '/teacher/dashboard'
      case 'STUDENT':
        return '/student/dashboard'
      case 'LIMITED_STUDENT':
        return '/limited-student/dashboard'
      default:
        return '/dashboard'
    }
  }
  
  const baseItems = [
    {
      key: 'dashboard',
      to: getDashboardRoute(),
      icon: 'mdi-view-dashboard'
    },
    {
      key: 'profile',
      to: '/profile',
      icon: 'mdi-account-circle'
    }
  ]

  // Admin: show a single entry to admin dashboard and a direct link to tests; hide duplicate generic dashboard
  if (user.value?.role === 'ADMIN') {
    return [
      { key: 'admin', to: '/admin', icon: 'mdi-shield-crown' },
      { key: 'users', to: '/admin/users', icon: 'mdi-account-group' },
      { key: 'studentAnalytics', to: '/admin/student-analytics', icon: 'mdi-chart-bar' },
      { key: 'testManagement', to: '/admin/tests', icon: 'mdi-clipboard-edit' },
      { key: 'lessons', to: '/admin/lessons', icon: 'mdi-video-box' },
      { key: 'schedule', to: '/admin/schedule', icon: 'mdi-calendar-account' },
      { key: 'adminActions', to: '/admin/actions', icon: 'mdi-history' },
      { key: 'serverStatus', to: '/admin/server-status', icon: 'mdi-server' },
      { key: 'errorLogs', to: '/admin/error-logs', icon: 'mdi-bug-outline' },
      { key: 'botSendMessage', to: '/admin/bot-send-message', icon: 'mdi-message-processing' },
      { key: 'aiSettings', to: '/admin/ai-settings', icon: 'mdi-robot' },
      { key: 'profile', to: '/profile', icon: 'mdi-account-circle' },
      { key: 'settings', to: '/admin/settings', icon: 'mdi-cog' }
    ]
  }

  const role = user.value?.role
  const items = [...baseItems]
  if (role === 'INSTRUCTOR' && user.value?.canViewAllSchedules === true) {
    items.splice(1, 0, {
      key: 'allSchedules',
      to: '/instructor/all-schedules',
      icon: 'mdi-calendar-edit'
    })
  }

  return items
})

// Methods
const navigateToSection = async (sectionId) => {
  // Егер басты бетте болмасақ, алдымен басты бетке өтеміз
  if (route.path !== '/') {
    await router.push('/')
    // Router жүктелуін күтеміз
    await new Promise(resolve => setTimeout(resolve, 300))
  }
  
  // Секцияға scroll жасаймыз
  const element = document.getElementById(sectionId)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

const isActiveRoute = (path) => {
  return route.path === path
}

const logout = async () => {
  try {
    await authStore.logout()
    mobileMenuOpen.value = false
    router.push('/')
  } catch (error) {
    snackbar.error(t('messages.errorOccurred'))
  }
}

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value
}

const handleMobileMenuClick = (item) => {
  mobileMenuOpen.value = false
  if (item.action) {
    item.action()
  }
}

const handleScroll = () => {
  scrolled.value = window.scrollY > 50
}

// Function to create short name from full name
const getShortName = (fullName) => {
  if (!fullName) return ''
  
  const names = fullName.trim().split(' ')
  if (names.length === 1) {
    // Return first two letters for single name
    return names[0].substring(0, 2).toUpperCase()
  }
  
  // Take first letter of first name and first letter of last name
  const firstNameInitial = names[0].charAt(0).toUpperCase()
  const lastNameInitial = names[names.length - 1].charAt(0).toUpperCase()
  
  return `${firstNameInitial}${lastNameInitial}`
}

// Lifecycle
onMounted(() => {
  window.addEventListener('scroll', handleScroll)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped lang="scss">
.main-navbar {
  overflow: visible !important;
}

.main-navbar :deep(.v-toolbar__content) {
  padding-inline: 0 !important;
}

.navbar-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  
  .left-section {
    display: flex;
    align-items: center;
    flex-direction: row-reverse; /* Brand-first: logo on the far left, menu next to it */
    gap: 20px;
  }
  
  .nav-menu {
    flex: 0 0 auto;
  }
  
  .logo-section {
    flex: 0 0 auto;
    display: flex;
  }
  
  .user-actions {
    flex: 0 0 auto;
  }
}

.logo-section {
  .logo-link {
    display: flex;
    align-items: center;
    text-decoration: none;
    color: inherit;
    margin-right: 0;

    &:hover .logo {
      transform: scale(1.05);
    }
  }
  
  .logo {
    border-radius: 50%;
    transition: transform 0.3s ease;
    margin-right: 12px;
  }
  
  .logo-text {
    .logo-title {
      margin: 0;
      font-size: 1.5rem;
      font-weight: 600;
      color: #FFC107;
    }
    
    .logo-subtitle {
      margin: 0;
      font-size: 0.875rem;
      opacity: 0.8;
    }
  }
  
  /* Variant 4 — connected logo + version pill */
  .mobile-home-icon.mobile-logo-pill {
    display: inline-flex;
    align-items: center;
    gap: 0;
    padding: 2px 10px 2px 2px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 193, 7, 0.35);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);

    .mobile-logo {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: #ffffff;
      padding: 2px;
      border: 2px solid rgba(255, 255, 255, 0.85);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
      cursor: pointer;
      transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      margin-right: 8px;

      &:hover {
        transform: scale(1.06);
      }
    }

    .mobile-version-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      border: none;
      background: transparent;
      color: rgba(255, 193, 7, 0.95);
      font-size: 0.72rem;
      font-weight: 700;
      letter-spacing: 0.04em;
      line-height: 1.1;
      white-space: nowrap;
    }
  }
}

.nav-menu {
  gap: 8px;
  
  .nav-dropdown-btn {
    text-transform: none;
    font-weight: 600;
    padding: 0 18px;
    height: 40px;
    border-radius: 999px;
    border: 1.5px solid rgba(255, 193, 7, 0.4);
    background: linear-gradient(45deg, rgba(255, 193, 7, 0.05), rgba(255, 193, 7, 0.1));
    transition: all 0.25s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    
    .chevron-icon {
      transition: transform 0.3s ease;
    }
    
    &:hover {
      background: linear-gradient(45deg, rgba(255, 193, 7, 0.15), rgba(255, 193, 7, 0.25));
      border-color: rgba(255, 193, 7, 0.6);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
      
      .chevron-icon {
        transform: rotate(180deg);
      }
    }
    
    .v-icon {
      color: #FFC107;
    }
  }
  
  .nav-dropdown {
    min-width: 280px;
    background: rgba(25, 25, 25, 0.98) !important;
    backdrop-filter: blur(20px);
    border: 2px solid rgba(255, 193, 7, 0.3);
    border-radius: 12px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
    padding: 12px 0;
    
    &::before {
      content: '';
      position: absolute;
      top: -2px;
      left: -2px;
      right: -2px;
      bottom: -2px;
      background: linear-gradient(45deg, rgba(255, 193, 7, 0.4), rgba(255, 193, 7, 0.1));
      border-radius: 12px;
      z-index: -1;
    }
    
    .v-list-item {
      min-height: 52px;
      padding: 12px 20px;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      margin: 2px 8px;
      border-radius: 8px;
      color: rgba(255, 255, 255, 0.9);
      position: relative;
      overflow: hidden;
      
      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 0;
        background: linear-gradient(90deg, rgba(255, 193, 7, 0.3), transparent);
        transition: width 0.3s ease;
      }
      
      .v-icon {
        color: rgba(255, 193, 7, 0.8);
        transition: all 0.3s ease;
        margin-right: 8px;
        filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
      }
      
      .v-list-item-title {
        font-weight: 500;
        font-size: 0.95rem;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
      }
      
      &:hover {
        background: rgba(255, 193, 7, 0.15) !important;
        color: #fff !important;
        transform: translateX(6px) scale(1.02);
        box-shadow: 0 4px 12px rgba(255, 193, 7, 0.2);
        
        &::before {
          width: 4px;
        }
        
        .v-icon {
          color: #FFC107 !important;
          transform: scale(1.15) rotate(5deg);
        }
      }
      
      &.v-list-item--active {
        background: rgba(255, 193, 7, 0.25) !important;
        color: #FFC107 !important;
        border-left: 3px solid #FFC107;
        font-weight: 600;
        box-shadow: 0 2px 8px rgba(255, 193, 7, 0.3);
        
        &::before {
          width: 4px;
          background: linear-gradient(90deg, rgba(255, 193, 7, 0.6), transparent);
        }
        
        .v-icon {
          color: #FFC107 !important;
          transform: scale(1.1);
        }
      }
    }
  }
}

.user-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.user-menu {
  /* Variant 4 — pill style consistent with Theme/Lang switchers */
  .user-btn.user-btn-pill {
    border-radius: 999px !important;
    min-width: 0 !important;
    padding: 4px 12px !important;
    height: 36px !important;

    :deep(.v-btn__content) {
      gap: 6px;
    }
  }

  .user-btn-avatar {
    border: 1px solid rgba(255, 193, 7, 0.4);
  }

  .user-btn {
    text-transform: none !important;
    padding: 8px 16px !important;
    height: auto !important;
    
    .user-info {
      text-align: left;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      
      .user-name {
        margin: 0 !important;
        font-size: 0.875rem !important;
        font-weight: 600 !important;
        line-height: 1.2 !important;
        color: inherit !important;
      }
      
      .user-role {
        margin: 0 !important;
        font-size: 0.75rem !important;
        opacity: 0.7 !important;
        line-height: 1.2 !important;
        color: inherit !important;
      }
    }
  }
  
  .user-dropdown {
    min-width: 200px;
    
    .logout-item {
      &:hover {
        background: rgba(244, 67, 54, 0.1);
      }
    }
  }
}

.auth-buttons {
  gap: 10px;
}

/* ============================================================
   Variant 4 — Desktop: uniform pill-style action buttons
   All right-side buttons (theme, lang, login, register, user)
   share the same 40px height and rounded pill border-radius.
   ============================================================ */
@media (min-width: 960px) {
  /* Icon-only buttons → 40×40 circle */
  .user-actions :deep(.theme-switcher),
  .user-actions :deep(.language-switcher) {
    width: 40px !important;
    min-width: 40px !important;
    height: 40px !important;
    padding: 0 !important;
    border-radius: 999px !important;
    border-width: 1.5px !important;
  }

  /* Login / Register buttons → 40px tall pill, auto width */
  .auth-buttons .v-btn {
    height: 40px !important;
    min-height: 40px !important;
    padding-inline: 18px !important;
    border-radius: 999px !important;
    border-width: 1.5px !important;
    font-weight: 600 !important;
    letter-spacing: 0.2px;
    text-transform: none !important;
  }

  /* Logged-in user button → 40px tall pill */
  .user-menu .user-btn.user-btn-pill {
    height: 40px !important;
    min-height: 40px !important;
    padding: 4px 14px 4px 6px !important;
    border-radius: 999px !important;
    border-width: 1.5px !important;
  }

  /* Tighten gap between buttons */
  .user-actions {
    gap: 10px;
  }
}

.mobile-menu-btn {
  z-index: 1005;
  
  &:hover {
    background: rgba(255, 193, 7, 0.1);
  }
}

.app-bar-scrolled {
  background: rgba(26, 26, 26, 0.95) !important;
  backdrop-filter: blur(10px);
}

.mobile-menu-dialog {
  z-index: 9999 !important;
  
  .mobile-menu-card {
    height: 100%;
    overflow-y: auto;
  }
  
  .mobile-header {
    padding: 24px;
    text-align: center;
    
    .mobile-user-info {
      h4 {
        margin: 8px 0 4px;
        font-size: 1.125rem;
      }
      
      p {
        margin: 0;
        font-size: 0.875rem;
      }
    }
    
    .mobile-auth-buttons {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
  }
  
  .logout-item {
    &:hover {
      background: rgba(244, 67, 54, 0.1);
    }
  }
}

@media (max-width: 959px) {
  .main-navbar {
    height: calc(60px + env(safe-area-inset-top, 0px)) !important;
    padding-top: env(safe-area-inset-top, 0px) !important;
  }

  .main-navbar :deep(.v-toolbar__content) {
    min-height: calc(60px + env(safe-area-inset-top, 0px)) !important;
    padding-top: env(safe-area-inset-top, 0px) !important;
  }

  .navbar-container {
    padding: 0 16px;
    min-height: 60px;
    min-width: 0;
    gap: 10px;
  }
  
  .logo-text .logo-subtitle {
    display: none;
  }
  
  .logo-section {
    flex: 0 0 auto;
    min-width: 0;
    
    .mobile-home-icon {
      min-height: 48px;
      width: auto;
    }
  }

  .user-actions {
    min-width: 0;
    gap: 6px;
  }

  /* Variant 4 — uniform 36×36 pill buttons (theme, lang, user) */
  .user-actions :deep(.theme-switcher),
  .user-actions :deep(.language-switcher),
  .user-menu .user-btn.user-btn-pill {
    width: 36px !important;
    min-width: 36px !important;
    height: 36px !important;
    padding: 0 !important;
    border-radius: 999px !important;
    border-width: 1.5px !important;
  }

  .user-actions :deep(.language-switcher .v-btn__content) {
    font-size: 0.72rem !important;
    font-weight: 700;
    line-height: 1 !important;
  }

  /* Legacy rule that no longer applies after Variant 4, but kept for null safety */
  .user-actions :deep(.language-switcher-legacy) {
    min-width: 42px !important;
    height: 34px !important;
    padding-inline: 10px !important;
    border-radius: 10px !important;
  }

  .user-actions :deep(.language-switcher-legacy .v-btn__content) {
    font-size: 0.78rem !important;
    line-height: 1 !important;
  }

  .user-menu .user-btn :deep(.v-btn__content) {
    gap: 4px !important;
  }

  .user-btn-avatar-img {
    width: 22px !important;
    height: 22px !important;
    border-radius: 50%;
    object-fit: cover;
  }

  .user-menu .user-btn :deep(.v-icon) {
    font-size: 16px !important;
  }

  .mobile-menu-btn {
    width: 36px;
    height: 36px;
    min-width: 36px !important;
  }
}

@media (max-width: 599px) {
  .main-navbar {
    height: calc(56px + env(safe-area-inset-top, 0px)) !important;
  }

  .logo-section .mobile-home-icon {
    gap: 6px;
  }

  .logo-section .mobile-home-icon .mobile-version-badge {
    min-width: 36px;
    padding: 2px 7px;
    font-size: 0.68rem;
  }

  .main-navbar :deep(.v-toolbar__content) {
    min-height: calc(56px + env(safe-area-inset-top, 0px)) !important;
  }

  .navbar-container {
    padding: 0 10px;
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 8px;
    
    .left-section {
      grid-column: 1;
      justify-self: start;
    }
    
    .nav-menu {
      display: none !important;
    }
    
    .user-actions {
      grid-column: 3;
      justify-self: end;
      gap: 6px;
    }
  }
  
  .logo-text .logo-title {
    font-size: 1.25rem;
  }
  
  .mobile-home-icon .mobile-logo {
    width: 36px;
    height: 36px;
  }

  /* Variant 4 — keep uniform 36×36 across all breakpoints */
  .user-actions :deep(.theme-switcher),
  .user-actions :deep(.language-switcher),
  .user-menu .user-btn.user-btn-pill {
    width: 36px !important;
    min-width: 36px !important;
    height: 36px !important;
    padding: 0 !important;
    border-radius: 999px !important;
  }

  .mobile-menu-btn {
    width: 34px;
    height: 34px;
    min-width: 34px !important;
  }
}

@media (max-width: 380px) {
  .navbar-container {
    padding: 0 8px;
    gap: 6px;
  }

  .user-actions {
    gap: 4px;
  }

  .mobile-home-icon .mobile-logo {
    width: 32px;
    height: 32px;
  }

  /* Variant 4 — slightly smaller uniform 32×32 on tiny phones */
  .user-actions :deep(.theme-switcher),
  .user-actions :deep(.language-switcher),
  .user-menu .user-btn.user-btn-pill,
  .mobile-menu-btn {
    width: 32px !important;
    min-width: 32px !important;
    height: 32px !important;
    padding: 0 !important;
    border-radius: 999px !important;
  }

  .user-actions :deep(.language-switcher .v-btn__content) {
    font-size: 0.68rem !important;
  }
}
</style>
